import { hasLocation } from "./msrLocations";

// LWI-1549 — client-side mirror of MsrSectionController::submitErrors so the
// Review step can show the same per-section problems before calling submit.
// Every rule is behind the server-driven pickpack required flags
// (usePickpackRequired), except LWI-1462 Authorized Name.

// LWI-1345 — same list as PartsTable.REASONS_REQUIRING_RGA / the server.
export const REASONS_REQUIRING_RGA = [
  "R&O Material - Returning repaired material",
  "R&O Material Not Repaired - Returning for Scrap",
  "Repair or Calibration & Return to Origin",
  "Return of Goods on DSP73",
  "Return to GDLS-C",
  "Return to Supplier",
];

const blank = (v) => String(v ?? "").trim() === "";

/** Does this Reason for Shipment make RGA/RMA # a required part field (LWI-1345)? */
export const rgaAppliesTo = (reasonForShipment) =>
  REASONS_REQUIRING_RGA.includes(String(reasonForShipment ?? "").trim());

/**
 * LWI-1560 — the required part fields a single line is missing, as labels,
 * behind the server-driven flags. Shared by the Review step, the parts grid's
 * status chip and the line drawer so all three agree with the submit gate.
 * @returns {string[]}
 */
export const missingPartFields = (
  part = {},
  required = {},
  rgaApplies = false
) => {
  const missing = [];
  const desc =
    part.description ?? part.ITEM_DESCRIPTION ?? part["ITEM DESCRIPTION"];
  const org = part.org_code ?? part.organization ?? part.orgCode;
  const qty = part.quantity ?? part.qty;
  const unitCost = part.unitCost ?? part.unit_cost;
  const rga = part.rgaRma ?? part.rga_rma;
  if (required.partDescription && blank(desc)) missing.push("Description");
  if (required.partOrganization && blank(org)) missing.push("Organization");
  if (required.partQuantity && !(Number(qty) > 0)) missing.push("Qty");
  if (required.partUom && blank(part.uom)) missing.push("UOM");
  if (
    required.partUnitCost &&
    (unitCost === undefined ||
      unitCost === null ||
      unitCost === "" ||
      parseFloat(unitCost) < 0)
  ) {
    missing.push("Unit Cost");
  }
  if (required.partRgaRma && rgaApplies && blank(rga))
    missing.push("RGA/RMA #");
  // LWI-1545 — Project/Program are per line; the LWI-1422 flags keep their names.
  if (required.project && blank(part.project)) missing.push("Project");
  if (required.program && blank(part.program)) missing.push("Program");
  return missing;
};

const truthy = (v) =>
  !!v && !["0", "no", "false", ""].includes(String(v).toLowerCase());

/**
 * LWI-1703 - `access` says which parts of the Shipment Classification step the
 * user can see; a rule for a hidden field is skipped (the server skips it too).
 * Omitted means both are visible.
 * @returns {Record<string, string[]>} section key → list of problems
 */
export const validateForSubmit = (
  details = {},
  parts = [],
  required = {},
  access = {}
) => {
  const { canViewGovernment = true, canViewClassification = true } = access;
  const errors = {};
  const add = (section, label) => {
    // (no `??=` — the project's ESLint parser predates logical assignment)
    if (!errors[section]) errors[section] = [];
    errors[section].push(label);
  };

  if (
    canViewClassification &&
    required.wipJob &&
    blank(details.msrContainsWipJob ?? details.wipJob)
  )
    add("classification", "MSR Contains WIP Job");
  if (required.shipFrom && !hasLocation(details.shipFrom))
    add("essentials", "Ship From");
  if (required.shipTo && !hasLocation(details.shipTo))
    add("essentials", "Ship To");
  if (required.chargeTo && !hasLocation(details.chargeTo))
    add("essentials", "Bill To");
  if (required.prepaidOrCollect && blank(details.prepaidOrCollect))
    add("essentials", "Prepaid or Collect");
  // LWI-1604 — Currency sits on the Parts tab now (the server still says "charges").
  if (required.currency && blank(details.currency)) add("parts", "Currency");
  if (required.reasonForShipment && blank(details.reasonForShipment))
    add("essentials", "Reason for Shipment");

  if (
    canViewGovernment &&
    truthy(details.governmentProperty) &&
    blank(details.authorizedName)
  ) {
    add("classification", "Authorized Name (required for Government Property)");
  }

  const rgaApplies = REASONS_REQUIRING_RGA.includes(
    String(details.reasonForShipment ?? "")
  );
  (Array.isArray(parts) ? parts : []).forEach((part, i) => {
    const label =
      part.ITEM_NUMBER ||
      part["ITEM NUMBER"] ||
      part.item_id ||
      `Part ${i + 1}`;
    const missing = missingPartFields(part, required, rgaApplies);
    if (missing.length) add("parts", `${label}: ${missing.join(", ")}`);
  });

  return errors;
};

/** Flatten a section-keyed error map into the order the sidebar lists sections. */
export const flattenErrors = (errors = {}) => Object.values(errors).flat();

/** Sections the server (or the client mirror) flagged, as sidebar marker keys. */
export const errorSections = (errors = {}) =>
  Object.keys(errors).filter((k) => (errors[k] || []).length > 0);
